/* Formulaire de création / édition d'un crédit immobilier.
 *
 * Utilisé depuis la liste (bouton "+ Ajouter un crédit") et depuis la fiche
 * détail (bouton "Modifier"). Le calcul des mensualités et de l'assurance est
 * réalisé côté backend au moment du POST/PATCH (fonction `_autofill`) — l'UI
 * affiche seulement une estimation en direct pour donner confiance à l'utilisateur.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const LOAN_TYPES = [
  { value: "amortissable", label: "Amortissable" },
  { value: "in_fine", label: "In fine" },
  { value: "relais", label: "Relais" },
  { value: "pret_travaux", label: "Prêt travaux" },
  { value: "revolving", label: "Revolving" },
  { value: "pret_aide", label: "Prêt aidé" },
  { value: "autre", label: "Autre" },
];
const RATE_TYPES = [
  { value: "fixe", label: "Fixe" },
  { value: "variable", label: "Variable" },
  { value: "mixte", label: "Mixte" },
];
const STATUSES = [
  { value: "active", label: "Actif" },
  { value: "pending", label: "En cours (déblocage à venir)" },
  { value: "closed", label: "Soldé" },
  { value: "refinanced", label: "Refinancé" },
  { value: "archived", label: "Archivé" },
];
const GUARANTEE_KINDS = [
  { value: "hypotheque", label: "Hypothèque" },
  { value: "caution_credit_logement", label: "Caution Crédit Logement" },
  { value: "nantissement", label: "Nantissement" },
  { value: "privilege_preteur", label: "Privilège prêteur (PPD)" },
  { value: "autre", label: "Autre" },
];

const NONE = "__none__"; // Radix Select refuse les valeurs vides -> sentinelle

function emptyForm() {
  return {
    label: "",
    reference: "",
    bank: "",
    borrower_company_id: "",
    building_id: "",
    loan_type: "amortissable",
    rate_type: "fixe",
    amount: "",
    down_payment: "",
    initial_duration_months: "",
    nominal_rate: "",
    insurance_rate: "",
    application_fees: "",
    signature_date: "",
    release_date: "",
    first_payment_date: "",
    guarantee_kind: "",
    guarantee_cost: "",
    guarantee_organism: "",
    status: "active",
    notes: "",
  };
}

function toForm(credit) {
  if (!credit) return emptyForm();
  const g = (credit.guarantees && credit.guarantees[0]) || {};
  return {
    label: credit.label || "",
    reference: credit.reference || "",
    bank: credit.bank || "",
    borrower_company_id: credit.borrower_company_id || "",
    building_id: (credit.building_ids || [])[0] || "",
    loan_type: credit.loan_type || "amortissable",
    rate_type: credit.rate_type || "fixe",
    amount: credit.amount ?? "",
    down_payment: credit.down_payment ?? "",
    initial_duration_months: credit.initial_duration_months ?? "",
    nominal_rate: credit.nominal_rate ?? "",
    insurance_rate: credit.insurance_rate ?? "",
    application_fees: credit.application_fees ?? "",
    signature_date: credit.signature_date || "",
    release_date: credit.release_date || "",
    first_payment_date: credit.first_payment_date || "",
    guarantee_kind: g.kind || "",
    guarantee_cost: g.cost ?? "",
    guarantee_organism: g.organism || "",
    status: credit.status || "active",
    notes: credit.notes || "",
  };
}

/** Estimation mensualité "amortissable" (calcul français) — indicatif. */
function estimateMonthly(amount, ratePct, months) {
  const P = parseFloat(amount);
  const r = parseFloat(ratePct);
  const n = parseInt(months, 10);
  if (!P || P <= 0 || !n || n <= 0) return null;
  if (!r || r <= 0) return P / n;
  const rm = r / 100 / 12;
  const factor = Math.pow(1 + rm, n);
  return (P * rm * factor) / (factor - 1);
}

function estimateInsurance(amount, insRatePct) {
  const P = parseFloat(amount);
  const r = parseFloat(insRatePct);
  if (!P || P <= 0 || !r || r <= 0) return 0;
  return (P * (r / 100)) / 12;
}

export default function CreditFormDialog({
  open,
  onOpenChange,
  credit = null,          // null => create, sinon edit
  onSaved,
  duplicateFrom = null,   // credit source pour préremplir en mode "dupliquer"
}) {
  const isEdit = !!credit;
  const [form, setForm] = useState(emptyForm);
  const [companies, setCompanies] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (credit) setForm(toForm(credit));
    else if (duplicateFrom) {
      const base = toForm(duplicateFrom);
      base.label = `${base.label} (copie)`;
      base.reference = "";
      setForm(base);
    } else setForm(emptyForm());
    // Reference data
    api.get("/companies").then(({ data }) => setCompanies(data.items || [])).catch(() => {});
    api.get("/buildings").then(({ data }) => setBuildings(data.items || [])).catch(() => {});
  }, [open, credit, duplicateFrom]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const estMonthly = useMemo(
    () => estimateMonthly(form.amount, form.nominal_rate, form.initial_duration_months),
    [form.amount, form.nominal_rate, form.initial_duration_months],
  );
  const estIns = useMemo(
    () => estimateInsurance(form.amount, form.insurance_rate),
    [form.amount, form.insurance_rate],
  );

  const buildPayload = () => {
    // Mode ÉDITION : envoyer les champs même vides pour permettre de les effacer.
    // Mode CRÉATION : envoyer uniquement les champs renseignés.
    const sendEmpty = isEdit;
    const p = {
      label: form.label.trim(),
      loan_type: form.loan_type,
      rate_type: form.rate_type,
      status: form.status,
    };
    // Chaînes libres — en édition on envoie "" pour permettre l'effacement.
    const stringFields = ["reference", "bank", "notes"];
    for (const k of stringFields) {
      const v = (form[k] || "").trim();
      if (v) p[k] = v;
      else if (sendEmpty) p[k] = "";
    }
    // Références (sélecteurs Select) — en édition on envoie "" pour délier.
    if (form.borrower_company_id) p.borrower_company_id = form.borrower_company_id;
    else if (sendEmpty) p.borrower_company_id = "";
    if (form.building_id) p.building_ids = [form.building_id];
    else if (sendEmpty) p.building_ids = [];
    // Numériques
    const numFields = [
      "amount", "down_payment", "nominal_rate",
      "insurance_rate", "application_fees",
    ];
    for (const k of numFields) {
      if (form[k] !== "") p[k] = parseFloat(form[k]);
      // Note : on ne peut PAS envoyer null à cause de exclude_none — donc les
      // numériques ne sont pas "effaçables". Comportement acceptable : mettre 0.
    }
    if (form.initial_duration_months !== "")
      p.initial_duration_months = parseInt(form.initial_duration_months, 10);
    // Dates
    const dateFields = ["signature_date", "release_date", "first_payment_date"];
    for (const k of dateFields) {
      if (form[k]) p[k] = form[k];
      else if (sendEmpty) p[k] = "";
    }
    // Garantie
    if (form.guarantee_kind) {
      const g = { kind: form.guarantee_kind };
      if (form.guarantee_cost !== "") g.cost = parseFloat(form.guarantee_cost);
      if (form.guarantee_organism.trim()) g.organism = form.guarantee_organism.trim();
      p.guarantees = [g];
    } else if (sendEmpty) {
      p.guarantees = [];
    }
    return p;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) { toast.error("Le nom du crédit est obligatoire"); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error("Le capital emprunté doit être positif"); return;
    }
    if (!form.initial_duration_months || parseInt(form.initial_duration_months, 10) <= 0) {
      toast.error("La durée en mois est obligatoire"); return;
    }
    // Le backend exige un taux nominal — on valide côté client pour un message clair.
    if (form.nominal_rate === "" || form.nominal_rate === null
        || form.nominal_rate === undefined
        || Number.isNaN(parseFloat(form.nominal_rate))) {
      toast.error("Le taux nominal annuel est obligatoire (mettre 0 pour un prêt à taux nul)");
      return;
    }
    if (parseFloat(form.nominal_rate) < 0) {
      toast.error("Le taux nominal ne peut pas être négatif"); return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      let result;
      if (isEdit) {
        await api.patch(`/credits/${credit.id}`, payload);
        result = { id: credit.id };
        toast.success("Crédit mis à jour");
      } else {
        const { data } = await api.post("/credits", payload);
        result = data;
        toast.success("Crédit créé");
      }
      onOpenChange(false);
      onSaved?.(result);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="credit-form-dialog"
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifier le crédit" : "Nouveau crédit"}</DialogTitle>
          <DialogDescription>
            Les mensualités et le tableau d&apos;amortissement sont recalculés automatiquement
            après enregistrement.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-6">
          {/* Identité */}
          <section>
            <div className="text-overline mb-3">Identité</div>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Nom du crédit *">
                <Input required value={form.label} onChange={(e) => update("label", e.target.value)}
                       placeholder="Ex : Crédit immeuble Lyon 3" data-testid="credit-form-label" />
              </Field>
              <Field label="Référence (interne)">
                <Input value={form.reference} onChange={(e) => update("reference", e.target.value)}
                       data-testid="credit-form-reference" />
              </Field>
              <Field label="Banque">
                <Input value={form.bank} onChange={(e) => update("bank", e.target.value)}
                       placeholder="Ex : Crédit Agricole" data-testid="credit-form-bank" />
              </Field>
              <Field label="Société emprunteuse">
                <Select
                  value={form.borrower_company_id || NONE}
                  onValueChange={(v) => update("borrower_company_id", v === NONE ? "" : v)}
                >
                  <SelectTrigger data-testid="credit-form-company"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>—</SelectItem>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.denomination || c.trade_name || c.name || "Sans nom"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Immeuble rattaché">
                <Select
                  value={form.building_id || NONE}
                  onValueChange={(v) => update("building_id", v === NONE ? "" : v)}
                >
                  <SelectTrigger data-testid="credit-form-building"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>—</SelectItem>
                    {buildings.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name || b.address || "Sans nom"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Statut">
                <Select value={form.status} onValueChange={(v) => update("status", v)}>
                  <SelectTrigger data-testid="credit-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </section>

          {/* Financier */}
          <section>
            <div className="text-overline mb-3">Caractéristiques financières</div>
            <div className="grid gap-3 md:grid-cols-3">
              <Field label="Type de prêt">
                <Select value={form.loan_type} onValueChange={(v) => update("loan_type", v)}>
                  <SelectTrigger data-testid="credit-form-loan-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LOAN_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Type de taux">
                <Select value={form.rate_type} onValueChange={(v) => update("rate_type", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RATE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Capital emprunté (€) *">
                <Input required type="number" min="0" step="0.01"
                       value={form.amount} onChange={(e) => update("amount", e.target.value)}
                       data-testid="credit-form-amount" />
              </Field>
              <Field label="Apport (€)">
                <Input type="number" min="0" step="0.01"
                       value={form.down_payment} onChange={(e) => update("down_payment", e.target.value)}
                       data-testid="credit-form-down-payment" />
              </Field>
              <Field label="Taux nominal annuel (%) *">
                <Input required type="number" min="0" step="0.01"
                       value={form.nominal_rate} onChange={(e) => update("nominal_rate", e.target.value)}
                       placeholder="Ex : 3.45" data-testid="credit-form-rate" />
              </Field>
              <Field label="Durée initiale (mois) *">
                <Input required type="number" min="1" max="600"
                       value={form.initial_duration_months}
                       onChange={(e) => update("initial_duration_months", e.target.value)}
                       placeholder="Ex : 240" data-testid="credit-form-duration" />
              </Field>
              <Field label="Taux assurance annuel (%)">
                <Input type="number" min="0" step="0.01"
                       value={form.insurance_rate} onChange={(e) => update("insurance_rate", e.target.value)}
                       placeholder="Ex : 0.36" data-testid="credit-form-insurance" />
              </Field>
              <Field label="Frais de dossier (€)">
                <Input type="number" min="0" step="0.01"
                       value={form.application_fees}
                       onChange={(e) => update("application_fees", e.target.value)}
                       data-testid="credit-form-fees" />
              </Field>
              <Field label="Signature">
                <Input type="date" value={form.signature_date}
                       onChange={(e) => update("signature_date", e.target.value)}
                       data-testid="credit-form-signature-date" />
              </Field>
              <Field label="Date de déblocage">
                <Input type="date" value={form.release_date}
                       onChange={(e) => update("release_date", e.target.value)}
                       data-testid="credit-form-release-date" />
              </Field>
              <Field label="Première échéance">
                <Input type="date" value={form.first_payment_date}
                       onChange={(e) => update("first_payment_date", e.target.value)}
                       data-testid="credit-form-first-payment" />
              </Field>
            </div>

            {(estMonthly !== null || estIns > 0) && (
              <div className="mt-3 rounded-xl bg-accent/5 border border-accent/20 p-3 text-xs text-muted-foreground">
                Estimation instantanée&nbsp;:
                {" "}<strong className="text-foreground">{estMonthly ? `${estMonthly.toFixed(2)} € /mois` : "—"}</strong>
                {" "}hors assurance
                {estIns > 0 && <> · assurance <strong className="text-foreground">{estIns.toFixed(2)} € /mois</strong></>}
                . Le calcul définitif est fait par le serveur (Decimal exact).
              </div>
            )}
          </section>

          {/* Garantie */}
          <section>
            <div className="text-overline mb-3">Garantie</div>
            <div className="grid gap-3 md:grid-cols-3">
              <Field label="Type de garantie">
                <Select
                  value={form.guarantee_kind || NONE}
                  onValueChange={(v) => update("guarantee_kind", v === NONE ? "" : v)}
                >
                  <SelectTrigger data-testid="credit-form-guarantee-kind"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>—</SelectItem>
                    {GUARANTEE_KINDS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Coût de la garantie (€)">
                <Input type="number" min="0" step="0.01"
                       value={form.guarantee_cost} onChange={(e) => update("guarantee_cost", e.target.value)} />
              </Field>
              <Field label="Organisme">
                <Input value={form.guarantee_organism}
                       onChange={(e) => update("guarantee_organism", e.target.value)}
                       placeholder="Ex : Crédit Logement" />
              </Field>
            </div>
          </section>

          {/* Notes */}
          <section>
            <Field label="Notes internes">
              <Textarea rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)}
                        data-testid="credit-form-notes" />
            </Field>
          </section>

          <DialogFooter className="pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={saving} data-testid="credit-form-submit">
              {saving ? "Enregistrement…" : isEdit ? "Enregistrer les modifications" : "Créer le crédit"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
